import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { CustomFieldType } from '@ee/common/enums';
import { BasicField, ConditionForm, CustomField, Operator, OperatorList } from '@ee/common/models';
import { STATES } from '@ee/common/constants';

/**
 * The shared "When" / condition half of a rule, extracted from
 * `CustomFieldRuleBuilderComponent`. It edits a single condition
 * (`source_field`, `operator`, `value`) including the per-type value editors and
 * operator curation, but deliberately excludes the intake-specific
 * `target_type`, `action`, and `target_field` controls.
 *
 * The host owns the `FormGroup<ConditionForm>` and passes it in via `form`.
 */
@Component({
  selector: 'ee-condition-builder',
  templateUrl: './condition-builder.component.html',
  styleUrls: ['./condition-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConditionBuilderComponent implements OnInit {
  /** The condition half of a RuleDefinition; owned by the host. */
  @Input({ required: true }) form!: FormGroup<ConditionForm>;
  /** The fields available to build a condition against. */
  @Input() sourceFields: CustomField[] = [];

  fieldTypes = CustomFieldType;
  sourceFieldType: CustomFieldType | undefined;
  operatorOptions: Operator[] = OperatorList;

  /** Search/selection model for the field autocomplete (holds typed text or the selected field). */
  readonly fieldSearch = new FormControl<BasicField | string | null>(null);

  ngOnInit(): void {
    // Sync local presentation state from any pre-loaded condition without mutating values.
    const field = this.form.controls.source_field.value;
    if (field) {
      this.sourceFieldType = field.type;
      this.operatorOptions = this.operatorsForType(field.type).options;
      this.fieldSearch.setValue(field, { emitEvent: false });
    }
  }

  /** Source fields filtered by the autocomplete's typed text (matches label or field key). */
  filteredSourceFields(): CustomField[] {
    const value = this.fieldSearch.value;
    const query = (typeof value === 'string' ? value : '').trim().toLowerCase();
    if (!query) {
      return this.sourceFields;
    }
    return this.sourceFields.filter(
      (f) => `${f.label ?? ''} ${f.field ?? ''}`.toLowerCase().includes(query)
    );
  }

  /** Renders the selected field as a readable label in the autocomplete input. */
  displayField = (field: BasicField | string | null): string => {
    if (!field) {
      return '';
    }
    return typeof field === 'string' ? field : `${field.label} (${field.field})`;
  };

  /** Apply the field chosen from the autocomplete. */
  onFieldSelected(event: MatAutocompleteSelectedEvent): void {
    this.updateSourceField(event.option.value as BasicField);
  }

  readableField(field: BasicField | null): string {
    if (!field) {
      return 'Select a Field';
    }
    return `${field?.label} (${field?.field})`;
  }

  latestOptions(field: BasicField | null): string[] {
    if (!field) {
      return [];
    }
    // find field in list of custom fields
    const f = this.sourceFields.find((cf) => cf.field === field.field);
    let options: string[] = f?.options ?? [];
    if (field.field === 'property_state') {
      options = Object.keys(STATES);
    }
    return options;
  }

  updateSourceField(field: BasicField): void {
    const currentField = this.form.controls.source_field.value;
    if (currentField?.type !== field.type) {
      this.form.controls.value.setValue(null);
    }

    // curate the operator options for the selected field's type
    const { options, defaultOperator } = this.operatorsForType(field.type);
    this.operatorOptions = options;
    this.updateOperator(defaultOperator);

    this.sourceFieldType = field.type;
    this.form.controls.source_field.setValue(field);
    this.fieldSearch.setValue(field, { emitEvent: false });
  }

  updateOperator(operator: Operator): void {
    const currentValue = this.form.controls.operator.value;

    if (currentValue === 'blank' && operator !== 'blank') {
      this.form.controls.value.addValidators(Validators.required);
      this.form.controls.value.updateValueAndValidity();
      this.form.controls.value.enable();
    } else if (currentValue !== 'blank' && operator === 'blank') {
      this.form.controls.value.clearValidators();
      this.form.controls.value.updateValueAndValidity();
      this.form.controls.value.setValue(null);
      this.form.controls.value.disable();
    }

    if (((currentValue === 'in' || currentValue === 'none') && operator !== 'in' && operator !== 'none') ||
      (currentValue !== 'in' && currentValue !== 'none' && (operator === 'in' || operator === 'none')) ||
      (currentValue === 'blank')) {
      this.form.controls.value.setValue(null);
    }
    this.form.controls.operator.setValue(operator);
  }

  readableOperator(operator: Operator): string {
    switch (operator) {
      case '==':
        return 'is equal to';
      case '<=':
        return 'is less than or equal to';
      case '>=':
        return 'is greater than or equal to';
      case '>':
        return 'is greater than';
      case '<':
        return 'is less than';
      case 'in':
        return 'is one of';
      case 'none':
        return 'is not one of';
      case 'blank':
        return 'is empty';
      case 'contains':
        return 'string contains';
      case 'not_contains':
        return 'string does not contain';
      case 'starts_with':
        return 'starts with';
      case 'ends_with':
        return 'ends with';
      default:
        return '';
    }
  }

  /**
   * The operator set (and a sensible default operator) for a given field type,
   * mirroring the curation logic of the original intake rule builder.
   */
  private operatorsForType(type: CustomFieldType): { options: Operator[]; defaultOperator: Operator } {
    if (type === CustomFieldType.TEXT || type === CustomFieldType.MULTI_TEXT) {
      return { options: ['==', 'blank', 'contains', 'not_contains', 'starts_with', 'ends_with'], defaultOperator: '==' };
    } else if (type === CustomFieldType.BOOLEAN) {
      return { options: ['==', 'blank'], defaultOperator: '==' };
    } else if (type === CustomFieldType.DATE) {
      return { options: ['==', '<=', '>=', '>', '<', 'blank'], defaultOperator: '==' };
    } else if (type === CustomFieldType.DECIMAL) {
      return { options: ['==', '<=', '>=', '>', '<', 'blank'], defaultOperator: '==' };
    } else if (type === CustomFieldType.SELECT || type === CustomFieldType.RADIO) {
      return { options: ['in', 'none', 'blank'], defaultOperator: 'in' };
    }
    return { options: OperatorList, defaultOperator: '==' };
  }

  protected readonly OperatorList = OperatorList;
}
